"use client";

import { useState } from "react";
import {
  chronoTicks,
  chronoTooltipBox,
  chronoX,
  derivePeers,
  fmtBigInt,
  fmtDate,
  fmtMillions,
  pinRadius,
  project,
  yearGap,
} from "@/app/climate/_lib/peer-map";

/**
 * Peer-declarations figure: a Canada-focused pin map of municipalities that
 * declared a climate emergency, plus a chronological strip making Calgary's
 * late entry visible against the rest. Aggregate counts (global + Canada)
 * sit in a header band; the world-first anchor (Darebin) is called out as
 * a sidebar note rather than rendered on the Canada projection.
 *
 * Province outlines are pre-projected to SVG paths in the build script
 * (see scripts/climate/peer_declarations.py). Pins and outlines share the
 * same lat/lon → pixel projection so they register pixel-for-pixel. The
 * projection is intentionally simple (linear over Canada's bounding box,
 * no Mercator stretch); per-pin coordinates are display positions only.
 */

export type PeerProvince = {
  pruid: string;
  name: string;
  abbr: string;
  path: string;
};

export type PeerGeometry = {
  viewbox: [number, number, number, number];
  provinces: PeerProvince[];
};

export type PeerPin = {
  id: string;
  name: string;
  country: "CA" | "AU" | string;
  region: string;
  level: "municipal" | "regional" | "national" | "legislative" | string;
  population: number | null;
  lat: number;
  lon: number;
  declared_on: string; // ISO YYYY-MM-DD
  motion_ref: string | null;
  motion_url: string | null;
  motion_verified: boolean;
  is_focus: boolean;
  note: string | null;
};

export type PeerAggregate = {
  jurisdictions: number;
  countries: number;
  population_covered: number;
  global_population_pct: number;
  national_governments_note: string;
  subnational_states_regions: number;
  canada_jurisdictions: number;
  canada_pinned: number;
  quebec_aggregate: {
    councils: number;
    tracker: string;
    tracker_url: string;
    since: string;
    population: number | null;
  };
  snapshot_at: string;
};

export type PeerDeclarationsPayload = {
  aggregate: PeerAggregate;
  pins: PeerPin[];
  geometry: PeerGeometry;
  world: import("./ClimateWorldMap").WorldPayload;
};

export function ClimatePeerMap({
  aggregate,
  pins,
  geometry,
}: {
  aggregate: PeerAggregate;
  pins: PeerPin[];
  geometry: PeerGeometry;
}) {
  const [hover, setHover] = useState<PeerPin | null>(null);
  const {
    canadianPins,
    chrono,
    calgary,
    darebin,
    earliestCa,
    calgaryLagDays: tCalgaryGapDays,
  } = derivePeers(pins);

  return (
    <div className="space-y-10">
      {/* Header band — global aggregate stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 sm:gap-6">
        <Stat
          label="# jurisdictions"
          value={fmtBigInt(aggregate.jurisdictions)}
          foot={`${aggregate.countries} countries · cedamia tracker, ${aggregate.snapshot_at}`}
          accent="var(--sky)"
        />
        <Stat
          label="people covered"
          value={`~${fmtMillions(aggregate.population_covered)}`}
          foot={`${aggregate.global_population_pct.toFixed(1)}% of world population`}
          accent="var(--sage)"
        />
        <Stat
          label="Canadian declarations"
          value={fmtBigInt(aggregate.canada_jurisdictions)}
          foot={`${aggregate.canada_pinned} pinned individually + ${aggregate.quebec_aggregate.councils} Québec councils tracked together`}
          accent="var(--mustard)"
        />
        <Stat
          label="Calgary's lag"
          value={
            tCalgaryGapDays > 0
              ? `+${(tCalgaryGapDays / 30.44).toFixed(0)} mo`
              : "—"
          }
          foot={
            earliestCa && calgary
              ? `${earliestCa.name} declared ${fmtDate(earliestCa.declared_on)}; Calgary ${fmtDate(calgary.declared_on)}`
              : "first vs. Calgary"
          }
          accent="var(--coral)"
        />
      </div>

      {/* Canada dot map */}
      <div>
        <div className="cs-mono text-ink-3 text-[11px] uppercase tracking-wider mb-2">
          Every individual Canadian declaration on the cedamia tracker
        </div>
        <div className="relative cs-card-paper bg-bg rounded-xl overflow-hidden">
          <svg
            viewBox={geometry.viewbox.join(" ")}
            width="100%"
            height="auto"
            role="img"
            aria-label="Map of Canadian municipalities that declared a climate emergency, with Calgary highlighted"
            className="block"
          >
            {/* Province outlines, projected by the build script using the
                same lat/lon → pixel mapping as the pins below. */}
            <g
              fill="var(--rule-1)"
              fillOpacity={0.35}
              stroke="var(--rule-2)"
              strokeWidth={0.6}
              vectorEffect="non-scaling-stroke"
            >
              {geometry.provinces.map((pr) => (
                <path key={pr.pruid} d={pr.path} aria-hidden="true" />
              ))}
            </g>

            {/* Province abbreviations, drawn over the fill so they remain
                legible. Hand-positioned at the south-of-60 settled belt. */}
            <g
              className="cs-mono"
              fill="var(--ink-3)"
              fontSize={10}
              opacity={0.7}
              pointerEvents="none"
            >
              {[
                { label: "BC", lat: 53.5, lon: -125 },
                { label: "AB", lat: 54.5, lon: -115.5 },
                { label: "SK", lat: 54.5, lon: -106 },
                { label: "MB", lat: 54.5, lon: -97.5 },
                { label: "ON", lat: 50, lon: -86 },
                { label: "QC", lat: 51, lon: -71 },
                { label: "NB", lat: 46.5, lon: -66.5 },
                { label: "NS", lat: 45, lon: -63 },
                { label: "NL", lat: 53, lon: -60 },
                { label: "YT", lat: 64, lon: -135 },
                { label: "NT", lat: 65, lon: -118 },
                { label: "NU", lat: 66, lon: -90 },
              ].map((p) => {
                const { x, y } = project(p.lat, p.lon);
                return (
                  <text
                    key={p.label}
                    x={x}
                    y={y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    {p.label}
                  </text>
                );
              })}
            </g>

            {/* Pins. Render non-focus first so Calgary lands on top.
                Sizes scaled tighter than v1 so 100+ pins don't overlap into
                an unreadable blob over Southern Ontario. */}
            {canadianPins
              .filter((p) => !p.is_focus)
              .map((p) => {
                const { x, y } = project(p.lat, p.lon);
                const r = pinRadius(p.population);
                return (
                  <PinDot
                    key={p.id}
                    x={x}
                    y={y}
                    r={r}
                    verified={p.motion_verified}
                    highlight={hover?.id === p.id}
                    level={p.level}
                  />
                );
              })}
            {calgary && (
              <CalgaryPin
                {...project(calgary.lat, calgary.lon)}
                name={calgary.name}
                date={fmtDate(calgary.declared_on)}
              />
            )}

            {/* Hit-targets — invisible larger circles drive hover state and
                keyboard focus. Native <title> remains as a screen-reader and
                no-JS fallback. */}
            <g>
              {canadianPins.map((p) => {
                const { x, y } = project(p.lat, p.lon);
                return (
                  <circle
                    key={`tt-${p.id}`}
                    cx={x}
                    cy={y}
                    r={11}
                    fill="transparent"
                    stroke="none"
                    tabIndex={0}
                    style={{ cursor: "pointer", outline: "none" }}
                    onMouseEnter={() => setHover(p)}
                    onMouseLeave={() => setHover(null)}
                    onFocus={() => setHover(p)}
                    onBlur={() => setHover(null)}
                  >
                    <title>
                      {`${p.name} (${p.region}) — declared ${fmtDate(p.declared_on)}${
                        p.motion_verified ? " · primary-verified" : ""
                      }`}
                    </title>
                  </circle>
                );
              })}
            </g>
          </svg>

          {/* Hover readout — engagement-style overlay anchored top-right
              (top-left is taken by Calgary's persistent label). */}
          <div
            className="absolute top-3 right-3 bg-white/92 backdrop-blur-sm border border-rule rounded px-3 py-2 text-sm pointer-events-none min-w-[200px] max-w-[260px]"
            aria-live="polite"
          >
            {hover ? (
              <>
                <div className="cs-mono text-ink-3 text-xs">
                  {hover.region}
                </div>
                <div className="font-medium leading-tight">{hover.name}</div>
                <div className="cs-num text-base mt-1">
                  {fmtDate(hover.declared_on)}
                </div>
                <div className="mt-1.5 text-[12px] cs-mono text-ink-3">
                  {hover.motion_verified
                    ? "primary-verified motion"
                    : "cedamia-tracked"}
                  {hover.motion_ref ? ` · ${hover.motion_ref}` : ""}
                </div>
                {hover.population != null ? (
                  <div className="mt-0.5 text-[12px] cs-mono text-ink-3">
                    pop. {fmtBigInt(hover.population)}
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className="cs-mono text-ink-3 text-xs">hover a pin</div>
                <div className="cs-num text-base mt-1">
                  {canadianPins.length}
                </div>
                <div className="cs-mono text-xs text-ink-3">
                  Canadian declarations shown
                </div>
              </>
            )}
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-ink-3 cs-mono">
          <span className="inline-flex items-center gap-2">
            <svg width={14} height={14} viewBox="0 0 14 14" aria-hidden>
              <circle cx={7} cy={7} r={4} fill="var(--coral)" />
            </svg>
            Calgary (focus)
          </span>
          <span className="inline-flex items-center gap-2">
            <svg width={14} height={14} viewBox="0 0 14 14" aria-hidden>
              <circle cx={7} cy={7} r={4} fill="var(--ink)" />
            </svg>
            primary-verified motion
          </span>
          <span className="inline-flex items-center gap-2">
            <svg width={14} height={14} viewBox="0 0 14 14" aria-hidden>
              <circle
                cx={7}
                cy={7}
                r={4}
                fill="none"
                stroke="var(--ink-3)"
                strokeWidth={1.2}
              />
            </svg>
            municipal / regional
          </span>
          <span className="inline-flex items-center gap-2">
            <svg width={14} height={14} viewBox="0 0 14 14" aria-hidden>
              <rect
                x={3}
                y={3}
                width={8}
                height={8}
                fill="none"
                stroke="var(--ink-2)"
                strokeWidth={1.5}
              />
            </svg>
            federal / legislative
          </span>
          <span className="text-ink-3">size ∝ √population</span>
        </div>

        {/* Quebec aggregate callout — 525 councils tracked under Groupe
            Mobilisation as a single cedamia row, so they can't be pinned
            individually. Surfaced here so the headline 653 reconciles with
            the visible pin count. */}
        <div className="mt-4 cs-card-paper bg-bg rounded-xl p-4 sm:p-5 flex flex-wrap items-baseline gap-x-5 gap-y-2">
          <span
            className="cs-display"
            style={{ fontSize: "1.6rem", lineHeight: 1 }}
          >
            +{aggregate.quebec_aggregate.councils}
          </span>
          <span className="text-ink-2 leading-snug flex-1 min-w-[12rem]">
            Québec municipal councils tracked together by{" "}
            <a
              href={aggregate.quebec_aggregate.tracker_url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-rule-2 hover:decoration-ink"
            >
              {aggregate.quebec_aggregate.tracker}
            </a>{" "}
            ({aggregate.quebec_aggregate.since.toLowerCase()}). cedamia carries
            them as a single aggregate row, so they appear in the headline
            count but not as individual pins on the map above.
          </span>
        </div>
      </div>

      {/* Chronological strip — every named Canadian pin laid out by date.
          Shares the hover state with the map above so a single mouseover
          highlights both the geographic pin and the timeline dot. */}
      <div>
        <div className="cs-mono text-ink-3 text-[11px] uppercase tracking-wider mb-2">
          When each declared — Calgary in coral
        </div>
        <ChronoStrip
          pins={chrono}
          activeId={hover?.id}
          onHover={setHover}
        />
      </div>

      {/* Global anchor — Darebin (Melbourne) called out as a sidebar note. */}
      {darebin && (
        <div className="cs-card-paper bg-bg rounded-xl p-5 sm:p-6">
          <div className="cs-mono text-ink-3 text-[11px] uppercase tracking-wider">
            Global first
          </div>
          <p className="mt-2 text-ink-2 leading-relaxed">
            The first municipal climate emergency declaration on record was
            passed by{" "}
            <strong className="text-ink">{darebin.name}</strong>{" "}
            (Victoria, Australia) on{" "}
            <strong className="text-ink">{fmtDate(darebin.declared_on)}</strong>
            , per cedamia&rsquo;s tracker — almost{" "}
            {calgary
              ? `${yearGap(darebin.declared_on, calgary.declared_on)} years`
              : "five years"}{" "}
            before Calgary&rsquo;s.
          </p>
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  foot,
  accent,
}: {
  label: string;
  value: string;
  foot: string;
  accent: string;
}) {
  // flex column + h-full so all four cards stretch to the tallest sibling and
  // the foot copy sticks to the card's bottom edge. Without this, a card with
  // a one-line foot leaves whitespace below the foot, which makes the foot of
  // the longer-foot cards (Canadian declarations, Calgary's lag) read as
  // misaligned next to the shorter ones.
  return (
    <div
      className="cs-card-paper bg-bg rounded-xl p-5 flex flex-col h-full"
      style={{ borderTop: `3px solid ${accent}` }}
    >
      <div className="cs-mono text-ink-3 text-[11px] uppercase tracking-wider">
        {label}
      </div>
      <div
        className="cs-display mt-2"
        style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)", lineHeight: 1.05 }}
      >
        {value}
      </div>
      <div className="mt-auto pt-3 text-[13px] leading-snug text-ink-3">
        {foot}
      </div>
    </div>
  );
}

function PinDot({
  x,
  y,
  r,
  verified,
  highlight,
  level,
}: {
  x: number;
  y: number;
  r: number;
  verified: boolean;
  highlight: boolean;
  level: string;
}) {
  // Federal / legislative declarations get a square so they don't get visually
  // confused with municipal pins. They're a different *kind* of declaration
  // and the map shouldn't pretend otherwise.
  if (level === "national" || level === "legislative") {
    const side = (r + 0.5) * 2;
    if (highlight) {
      return (
        <g transform={`translate(${x} ${y})`}>
          <rect
            x={-side / 2 - 3}
            y={-side / 2 - 3}
            width={side + 6}
            height={side + 6}
            fill="var(--ink)"
            opacity={0.18}
          />
          <rect
            x={-side / 2}
            y={-side / 2}
            width={side}
            height={side}
            fill="var(--ink)"
          />
        </g>
      );
    }
    return (
      <rect
        x={x - side / 2}
        y={y - side / 2}
        width={side}
        height={side}
        fill="var(--bg)"
        stroke="var(--ink-2)"
        strokeWidth={1.5}
      />
    );
  }
  if (highlight) {
    return (
      <g transform={`translate(${x} ${y})`}>
        <circle r={r + 4} fill="var(--ink)" opacity={0.18} />
        <circle r={r} fill="var(--ink)" />
      </g>
    );
  }
  if (verified) {
    return <circle cx={x} cy={y} r={r} fill="var(--ink)" />;
  }
  return (
    <circle
      cx={x}
      cy={y}
      r={r}
      fill="var(--bg)"
      stroke="var(--ink-3)"
      strokeWidth={1.2}
    />
  );
}

function CalgaryPin({
  x,
  y,
  name,
  date,
}: {
  x: number;
  y: number;
  name: string;
  date: string;
}) {
  return (
    <g>
      <circle cx={x} cy={y} r={12} fill="var(--coral)" opacity={0.18} />
      <circle cx={x} cy={y} r={6} fill="var(--coral)" />
      <text
        x={x + 10}
        y={y - 8}
        fontSize={12}
        className="cs-mono"
        fill="var(--ink)"
      >
        {name}
      </text>
      <text
        x={x + 10}
        y={y + 6}
        fontSize={10}
        className="cs-mono"
        fill="var(--ink-3)"
      >
        {date}
      </text>
    </g>
  );
}

function ChronoStrip({
  pins,
  activeId,
  onHover,
}: {
  pins: PeerPin[];
  activeId: string | undefined;
  onHover: (p: PeerPin | null) => void;
}) {
  if (pins.length === 0) return null;
  const tMin = Date.parse(pins[0].declared_on);
  const tMax = Date.parse(pins[pins.length - 1].declared_on);
  const span = Math.max(1, tMax - tMin);
  const stripHeight = 80;
  const stripWidth = 880;
  const padX = 24;
  const usableW = stripWidth - padX * 2;
  const baselineY = stripHeight / 2 + 10;

  const activePin = activeId
    ? pins.find((p) => p.id === activeId) ?? null
    : null;
  const activeX = activePin
    ? chronoX(Date.parse(activePin.declared_on), tMin, span, padX, usableW)
    : 0;

  const ticks = chronoTicks(tMin, tMax, span, padX, usableW);

  return (
    <svg
      viewBox={`0 0 ${stripWidth} ${stripHeight + 24}`}
      width="100%"
      height="auto"
      role="img"
      aria-label="Chronological order of Canadian climate emergency declarations, with Calgary highlighted"
      className="cs-card-paper bg-bg rounded-xl"
    >
      {/* Baseline */}
      <line
        x1={padX}
        x2={stripWidth - padX}
        y1={stripHeight / 2 + 10}
        y2={stripHeight / 2 + 10}
        stroke="var(--rule-2)"
        strokeWidth={1}
      />
      {/* Year ticks */}
      <g
        className="cs-mono"
        fill="var(--ink-3)"
        fontSize={10}
      >
        {ticks.map((t) => (
          <g key={t.year}>
            <line
              x1={t.x}
              x2={t.x}
              y1={stripHeight / 2 + 6}
              y2={stripHeight / 2 + 14}
              stroke="var(--rule-2)"
            />
            <text x={t.x} y={stripHeight + 18} textAnchor="middle">
              {t.year}
            </text>
          </g>
        ))}
      </g>
      {/* Pins. Active highlight rendered via halo so hover-from-map and
          hover-from-strip both light up the matching dot here. */}
      {pins.map((p) => {
        const t = Date.parse(p.declared_on);
        const x = chronoX(t, tMin, span, padX, usableW);
        const y = stripHeight / 2 + 10;
        const isActive = activeId === p.id;
        if (p.is_focus) {
          return (
            <g key={p.id}>
              <line
                x1={x}
                x2={x}
                y1={y - 26}
                y2={y}
                stroke="var(--coral)"
                strokeWidth={1.5}
              />
              {isActive ? (
                <circle cx={x} cy={y} r={9} fill="var(--coral)" opacity={0.2} />
              ) : null}
              <circle cx={x} cy={y} r={5} fill="var(--coral)" />
              <text
                x={x}
                y={y - 30}
                fontSize={11}
                className="cs-mono"
                fill="var(--coral)"
                textAnchor="middle"
              >
                Calgary
              </text>
            </g>
          );
        }
        return (
          <g key={p.id}>
            {isActive ? (
              <circle cx={x} cy={y} r={8} fill="var(--ink)" opacity={0.2} />
            ) : null}
            <circle
              cx={x}
              cy={y}
              r={p.motion_verified ? 4 : 3}
              fill={p.motion_verified ? "var(--ink)" : "var(--bg)"}
              stroke="var(--ink-3)"
              strokeWidth={p.motion_verified ? 0 : 1}
            />
            {/* Wider invisible hit-target so small dots are easy to grab.
                Native <title> remains for screen readers + no-JS fallback. */}
            <circle
              cx={x}
              cy={y}
              r={9}
              fill="transparent"
              tabIndex={0}
              style={{ cursor: "pointer", outline: "none" }}
              onMouseEnter={() => onHover(p)}
              onMouseLeave={() => onHover(null)}
              onFocus={() => onHover(p)}
              onBlur={() => onHover(null)}
            >
              <title>{`${p.name} — ${fmtDate(p.declared_on)}`}</title>
            </circle>
          </g>
        );
      })}
      {/* Inline tooltip near the active dot. Rendered last so it sits above
          all the dots; pointer-events disabled so it doesn't capture mouse-out. */}
      {activePin && !activePin.is_focus ? (
        <ChronoTooltip
          x={activeX}
          y={baselineY}
          name={activePin.name}
          date={fmtDate(activePin.declared_on)}
          stripWidth={stripWidth}
          padX={padX}
        />
      ) : null}
    </svg>
  );
}

function ChronoTooltip({
  x,
  y,
  name,
  date,
  stripWidth,
  padX,
}: {
  x: number;
  y: number;
  name: string;
  date: string;
  stripWidth: number;
  padX: number;
}) {
  const { tx, ty, w, h, pointerX } = chronoTooltipBox(
    x,
    y,
    name,
    date,
    stripWidth,
    padX,
  );
  return (
    <g pointerEvents="none">
      <line
        x1={pointerX}
        x2={x}
        y1={ty + h}
        // dotR (6) + 1 — pointer line stops just above the dot.
        y2={y - 6 - 1}
        stroke="var(--ink-3)"
        strokeWidth={0.8}
      />
      <rect
        x={tx}
        y={ty}
        width={w}
        height={h}
        rx={4}
        ry={4}
        fill="white"
        stroke="var(--rule-2)"
        strokeWidth={1}
      />
      <text
        x={tx + 8}
        y={ty + 15}
        fontSize={11.5}
        fill="var(--ink)"
        style={{ fontWeight: 500 }}
      >
        {name}
      </text>
      <text
        x={tx + 8}
        y={ty + 30}
        fontSize={10.5}
        className="cs-mono"
        fill="var(--ink-3)"
      >
        {date}
      </text>
    </g>
  );
}
